import { useCurrentUser } from '@qualio/ui-components';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { QualioDocument } from '../../../../api/model/document';
import { useCollabServerConnectionManager } from '../../../../hooks/CollabServerConnectionManager';
import { isCrossCompanyDocument, isOwner } from '../../../../util/CurrentUser';
import { DocumentHTMLViewer } from '../../DocumentHTMLViewer/DocumentHTMLViewer';
import { FileDocumentEditor } from '../../FileDocumentEditor/index';
import { InlineAttachmentPreviewModal } from '../../InlineAttachmentPreview/InlineAttachmentPreviewModal';
import { DocumentOverviewContext } from '../Context';
import { DocEditorSideBarWrapper } from '../DocumentEditor/DocEditorSideBarWrapper';
import { DocumentSectionsManager } from '../DocumentEditor/DocumentSectionsManager/DocumentSectionsManager';
import { QMultiRootEditor } from '../QMultiRootEditor/QMultiRootEditor';
import { shouldShowFileUpload } from '../RenderLogic';

enum ContentComponentName {
  CKE_EDITOR,
  HTML_VIEWER,
  FILE_UPLOAD,
}

export const DocumentVariantRenderer = forwardRef<HTMLDivElement>((_, ref) => {
  const [attachmentForPreview, setAttachmentForPreview] = useState<string>();

  const currentUser = useCurrentUser();

  const showAttachmentForPreviewCallback = useCallback(
    (attachmentId: string) => {
      setAttachmentForPreview(attachmentId);
    },
    [],
  );

  const { qualioDocument, commentsPermission } = useContext(
    DocumentOverviewContext,
  );

  const shouldShowMultiRootEditor =
    qualioDocument.editor_version?.includes('multiroot');

  const { handleSessionDisconnectionEvent } =
    useCollabServerConnectionManager(qualioDocument);

  const determineContentComponentToShow = (
    commentsPermission: number,
    qualioDocument: QualioDocument,
  ): ContentComponentName => {
    if (shouldShowFileUpload(qualioDocument)) {
      return ContentComponentName.FILE_UPLOAD;
    }

    const hasEditPermission = commentsPermission > 0;
    if (
      isCrossCompanyDocument(currentUser, qualioDocument) ||
      !hasEditPermission ||
      qualioDocument.status_id === 'deleted' ||
      qualioDocument.status_id === 'effective' ||
      !qualioDocument.is_most_recent_version
    ) {
      return ContentComponentName.HTML_VIEWER;
    } else {
      return ContentComponentName.CKE_EDITOR;
    }
  };

  const componentToShow = determineContentComponentToShow(
    commentsPermission,
    qualioDocument,
  );

  useEffect(() => {
    if (componentToShow === ContentComponentName.HTML_VIEWER) {
      CKReusableContent?.ReusableContent?.createIframeResizer(
        process.env.REACT_APP_URL_ORIGIN,
      );
    }
    return () => {
      if (componentToShow === ContentComponentName.HTML_VIEWER) {
        CKReusableContent?.ReusableContent?.destroyIframeResizer(
          process.env.REACT_APP_URL_ORIGIN,
        );
      }
    };
  }, [componentToShow]);

  return (
    <>
      {attachmentForPreview && (
        <InlineAttachmentPreviewModal
          currentUser={currentUser}
          attachmentId={attachmentForPreview}
          onClose={() => setAttachmentForPreview(undefined)}
        />
      )}
      {componentToShow === ContentComponentName.FILE_UPLOAD && (
        <FileDocumentEditor
          currentUser={currentUser}
          fileDocument={qualioDocument}
          isOwner={isOwner(currentUser, qualioDocument)}
          commentsPermission={commentsPermission ?? 0}
          handleSessionDisconnectionEvent={handleSessionDisconnectionEvent}
        />
      )}
      {componentToShow === ContentComponentName.HTML_VIEWER && (
        <DocumentHTMLViewer
          viewDocument={qualioDocument}
          showTOC={true}
          showSidebar={false}
          setLoaded={true}
          handleAttachmentClick={showAttachmentForPreviewCallback}
        />
      )}
      {componentToShow === ContentComponentName.CKE_EDITOR &&
        !shouldShowMultiRootEditor && (
          <DocEditorSideBarWrapper ref={ref}>
            <DocumentSectionsManager
              handleAttachmentClick={showAttachmentForPreviewCallback}
              handleSessionDisconnectionEvent={handleSessionDisconnectionEvent}
            />
          </DocEditorSideBarWrapper>
        )}
      {componentToShow === ContentComponentName.CKE_EDITOR &&
        shouldShowMultiRootEditor && (
          <QMultiRootEditor
            handleAttachmentClick={showAttachmentForPreviewCallback}
            handleSessionDisconnectionEvent={handleSessionDisconnectionEvent}
          />
        )}
    </>
  );
});
